/**
 * Token Storage Utilities
 * Extracted from authService.ts
 *
 * SECURITY NOTE:
 * Access tokens and refresh tokens are held ONLY in-memory on the AuthService
 * singleton.  They are NOT persisted to localStorage or sessionStorage because
 * browser storage is accessible to any script running on the same origin and
 * is therefore vulnerable to XSS exfiltration.
 *
 * Long-lived session continuity across page reloads should be provided by
 * httpOnly, Secure, SameSite cookies set by the backend — not by client-side
 * storage.
 *
 * This module stores only non-secret session metadata (user profile, storage
 * type preference) so that the UI can restore display state quickly on load.
 *
 * // Security: tokens must only live in httpOnly cookies or in-memory
 */

import type { User } from "../services/authService";
import { log } from "@/shared/utils/logger";

export interface StoredTokens {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
}

const TOKEN_KEYS = ["accessToken", "refreshToken", "authToken"] as const;

type StorageType = "local" | "session";

// Explicit session decisions outrank stale or unavailable browser metadata.
// Undefined means no in-memory decision yet, so a later read can retry storage.
let preferredStorageType: StorageType | undefined;
let sessionUser: User | null | undefined;

/**
 * Non-secret, in-memory owner decision for scoped UI preferences.
 * Undefined means no explicit decision yet (legacy metadata may be consulted).
 * Null means explicitly cleared/logged out and must suppress legacy fallback.
 * A string is the latest saved/updated user ID, even if storage writes failed.
 * This read performs no storage access or retries and grants no API authority.
 */
export function getSessionUserIdDecision(): string | null | undefined {
  return sessionUser === undefined ? undefined : sessionUser?.id ?? null;
}

function withStorage<T>(type: StorageType, operation: (storage: Storage) => T): T | null {
  try {
    return operation(type === "local" ? localStorage : sessionStorage);
  } catch {
    // Both the browser accessor and individual methods can throw (including quota).
    return null;
  }
}

function removeEntry(type: StorageType, key: string): void {
  withStorage(type, (storage) => storage.removeItem(key));
}

function clearLegacyTokenEntries(): void {
  for (const type of ["local", "session"] as const) {
    for (const key of TOKEN_KEYS) removeEntry(type, key);
  }
}

/** Return the preferred metadata store, or null when its accessor is denied. */
export function getStorage(): Storage | null {
  return withStorage(getStorageType(), (storage) => storage);
}

export function getStorageType(): StorageType {
  if (preferredStorageType) return preferredStorageType;
  const storedType = withStorage("local", (storage) => storage.getItem("authStorageType"));
  if (storedType === "local" || storedType === "session") {
    preferredStorageType = storedType;
  }
  return preferredStorageType ?? "session";
}

function saveUser(user: User): void {
  const type = getStorageType();
  sessionUser = user;
  clearLegacyTokenEntries();
  // Remove old profiles first so a quota failure cannot leave stale metadata.
  for (const storeType of ["local", "session"] as const) removeEntry(storeType, "user");
  withStorage(type, (storage) => storage.setItem("user", JSON.stringify(user)));
  withStorage("local", (storage) => storage.setItem("authStorageType", type));
}

/**
 * Save user data and storage preference.
 * Tokens are NOT persisted — they live only in-memory.
 */
export function saveTokens(
  _accessToken: string,
  _refreshToken: string,
  user: User,
  rememberMe: boolean
): void {
  preferredStorageType = rememberMe ? "local" : "session";
  saveUser(user);
}

/**
 * Update existing session metadata in storage (maintains storage type).
 * Tokens are NOT persisted — they live only in-memory.
 */
export function updateTokens(
  _accessToken: string,
  _refreshToken: string,
  user: User
): void {
  saveUser(user);
}

/**
 * Load session metadata from storage.
 * Access and refresh tokens are no longer stored in web storage, so they
 * will always return null here.  The AuthService constructor should rely
 * on httpOnly cookies or a /auth/reload endpoint to restore a session.
 */
export function loadTokens(): StoredTokens {
  return {
    accessToken: null,
    refreshToken: null,
    user: getStoredUser(),
  };
}

/**
 * Clear all session data from both storages
 */
export function clearTokens(): void {
  // Invalidate memory before attempting browser cleanup. Failed removal must
  // never rehydrate the old profile in this runtime when storage recovers.
  sessionUser = null;
  preferredStorageType = "session";
  clearLegacyTokenEntries();
  for (const type of ["local", "session"] as const) {
    removeEntry(type, "user");
    removeEntry(type, "authStorageType");
  }
}

/**
 * Get access token — always returns null.
 * Tokens are no longer stored in web storage; use authService.getToken() instead.
 */
export function getStoredAccessToken(): string | null {
  clearLegacyTokenEntries();
  return null;
}

/**
 * Get refresh token — always returns null.
 * Tokens are no longer stored in web storage; use the in-memory AuthService.
 */
export function getStoredRefreshToken(): string | null {
  clearLegacyTokenEntries();
  return null;
}

export function getStoredUser(): User | null {
  if (sessionUser === null) {
    // Retry a logout cleanup that may have failed during a temporary outage.
    clearTokens();
    return null;
  }
  clearLegacyTokenEntries();
  if (sessionUser !== undefined) return sessionUser;
  const type = getStorageType();
  const storedUser = withStorage(type, (storage) => storage.getItem("user"));
  if (!storedUser) return null;

  try {
    return JSON.parse(storedUser);
  } catch (error) {
    log.error("tokenStorage", "Failed to parse stored user:", error);
    removeEntry(type, "user");
    return null;
  }
}
